// Сверка фискализации — GET /v1/reports/fiscal-reconciliation. v1: сверяем
// внутренний факт (sessions/cashOps/refunds уже хранят результат фискализации
// на каждой операции, просто нигде не был показан) — не независимая сверка с
// внешним отчётом кассы, но ловит реальные проблемы: чек не пробился, касса
// вернула ошибку. Внешняя сверка (официальный отчёт iKassa) появится, когда
// у клуба включена облачная интеграция (настраивается в Super Admin → Клубы) —
// см. externalAvailable ниже.

import { useState } from 'react'
import type { FiscalReconciliationReport } from '@billiard/shared'
import { useAuth } from '@/auth/AuthContext'
import { useFetch } from '@/hooks/useApi'
import { AsyncState, EmptyState, Field, Input, Kpi, SectionHead } from '@/components/ui'
import { count, dateTime, dateInputToMs, money, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'

const DAY_MS = 24 * 60 * 60 * 1000

const OP_LABEL: Record<string, string> = {
  deposit: 'Внесение',
  withdraw: 'Изъятие',
  refund: 'Возврат'
}

const STATE_LABEL: Record<string, string> = {
  pending: 'В ожидании',
  failed: 'Ошибка'
}

export function Reconciliation(): React.JSX.Element {
  const { isSuperAdmin } = useAuth()
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))

  const from = dateInputToMs(fromStr)
  const to = dateInputToMs(toStr) + DAY_MS - 1

  const report = useFetch<FiscalReconciliationReport>(
    '/v1/reports/fiscal-reconciliation',
    { from, to },
    [from, to]
  )

  return (
    <>
      <div className="eyebrow">Финансы</div>
      <h2 className="page-title">Сверка фискализации</h2>
      <p className="page-lead">
        Продажи без фискального документа, а также внесения/изъятия и возвраты, по которым касса не
        подтвердила чек. Данные — из уже сохранённого результата фискализации, показ ничего не
        меняет и не пробивает чеки заново.
      </p>

      <div className="filters" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '16px 0' }}>
        <Field label="С" htmlFor="rc-from">
          <Input id="rc-from" type="date" value={fromStr} onChange={(e) => setFromStr(e.target.value)} />
        </Field>
        <Field label="По" htmlFor="rc-to">
          <Input id="rc-to" type="date" value={toStr} onChange={(e) => setToStr(e.target.value)} />
        </Field>
      </div>

      <AsyncState query={report} loadingLabel="Сверяем период…" />

      {report.data && (
        <>
          {!report.data.externalAvailable && (
            <div className="placeholder" style={{ marginBottom: 20 }}>
              <div className="placeholder__mark">☁</div>
              <div className="badge">Только внутренняя сверка</div>
              <div>
                Сверяем то, что уже сохранено в базе по каждой операции. Сверка с официальным отчётом
                кассы из АИС ПКС iKassa появится, когда {isSuperAdmin ? 'вы включите' : 'платформа включит'}{' '}
                облачную интеграцию клуба{isSuperAdmin ? ' (Клубы → карточка клуба → iKassa)' : ''}.
              </div>
            </div>
          )}

          <SectionHead
            title="Продажи без чека"
            meta={`${count(report.data.sessions.withoutDoc)} из ${count(report.data.sessions.total)}`}
          />
          <div className="kpi-grid" style={{ marginBottom: 4 }}>
            <Kpi label="Всего продаж" value={count(report.data.sessions.total)} />
            <Kpi label="С чеком" value={count(report.data.sessions.withDoc)} />
            <Kpi
              label="Без чека"
              value={count(report.data.sessions.withoutDoc)}
              sub={report.data.sessions.withoutDoc > 0 ? 'Требует проверки' : undefined}
            />
          </div>
          {report.data.sessions.withoutDoc === 0 ? (
            <EmptyState message="За период все оплаченные продажи фискализированы." />
          ) : (
            <div className="table-wrap">
              <table className="rtable">
                <thead>
                  <tr>
                    <th>Чек</th>
                    <th>Смена</th>
                    <th>Закрыт</th>
                    <th className="num">Сумма</th>
                    <th>Оплата</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.sessions.items.map((s) => (
                    <tr key={s.id}>
                      <td>№{s.receiptNumber}</td>
                      <td>{s.shiftNumber != null ? `№${s.shiftNumber}` : '—'}</td>
                      <td>{dateTime(s.endedAt)}</td>
                      <td className="num">
                        {money(s.amount)} <Cur />
                      </td>
                      <td>{s.paymentMethod ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <SectionHead
            title="Внесения и изъятия"
            meta={`${count(report.data.cashOps.failed + report.data.cashOps.pending)} проблемных из ${count(report.data.cashOps.total)}`}
          />
          <ProblemOpsTable
            rows={report.data.cashOps.items}
            emptyMessage="Все запрошенные фискальные чеки по внесениям/изъятиям подтверждены."
          />

          <SectionHead
            title="Возвраты"
            meta={`${count(report.data.refunds.failed + report.data.refunds.pending)} проблемных из ${count(report.data.refunds.total)}`}
          />
          <ProblemOpsTable
            rows={report.data.refunds.items}
            emptyMessage="Все возвраты с фискальным чеком подтверждены."
          />
        </>
      )}
    </>
  )
}

function ProblemOpsTable({
  rows,
  emptyMessage
}: {
  rows: FiscalReconciliationReport['cashOps']['items']
  emptyMessage: string
}): React.JSX.Element {
  if (rows.length === 0) return <EmptyState message={emptyMessage} />
  return (
    <div className="table-wrap">
      <table className="rtable">
        <thead>
          <tr>
            <th>Тип</th>
            <th>Смена</th>
            <th>Время</th>
            <th className="num">Сумма</th>
            <th>Причина</th>
            <th>Статус кассы</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{OP_LABEL[r.kind] ?? r.kind}</td>
              <td>{r.shiftNumber != null ? `№${r.shiftNumber}` : '—'}</td>
              <td>{dateTime(r.ts)}</td>
              <td className="num">
                {money(r.amount)} <Cur />
              </td>
              <td>{r.reason ?? '—'}</td>
              <td>
                <span
                  className="pill"
                  style={{
                    background: 'rgba(245,158,11,0.12)',
                    color: 'var(--amber)',
                    border: '1px solid rgba(245,158,11,0.3)'
                  }}
                  title={r.fiscalError ?? undefined}
                >
                  {STATE_LABEL[r.fiscalState] ?? r.fiscalState}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
